<script setup lang="ts">
import { ButtonType, CustomButton } from '@/components/common';
import type { DynamicRowConfig, MatrixRow } from '@/models';
import Dialog from 'primevue/dialog';
import ToggleSwitch from 'primevue/toggleswitch';
import Checkbox from 'primevue/checkbox';
import { onMounted, ref, type PropType } from 'vue';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  row: {
    type: Object as PropType<MatrixRow>
  }
});

const dynamicConfig = ref(false);
const dynamicConfigOptions = ref<DynamicRowConfig | undefined>();
const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = () => {
  emits('onClose');
};

onMounted(() => {
  if (props.row) {
    dynamicConfig.value = props.row.dynamicRowConfig ? true : false;
    dynamicConfigOptions.value = props.row.dynamicRowConfig;
  }
});

const onDynamicConfigSwitch = () => {
  if (!dynamicConfig.value) {
    dynamicConfigOptions.value = undefined;
  } else {
    dynamicConfigOptions.value = {
      copyRowData: false,
      copyRowHeader: false
    };
  }
};

const saveConfig = () => {
  emits('onSave', dynamicConfigOptions.value);
};
</script>

<template>
  <div class="dialog-root">
    <Dialog :visible="visible"
      header="Dynamic row config"
      modal
      class="dialog"
      append-to="self"
      @update:visible="closeDialog">
      <div class="dialog-content">
        <div class="enable-dynamic">
          <div class="enable-dynamic-text">Enable Dynamic Rows</div>
          <ToggleSwitch v-model="dynamicConfig"
            @change="onDynamicConfigSwitch"/>
        </div>
        <div class="dynamic-options" v-if="dynamicConfig && dynamicConfigOptions">
          <Checkbox v-model="dynamicConfigOptions.copyRowData"
            inputId="copy-row-data"
            name="copy-row-data"
            :binary="true"/>
          <div class="group-header">Copy row data</div>
        </div>

        <div class="dynamic-options" v-if="dynamicConfig && dynamicConfigOptions">
          <Checkbox v-model="dynamicConfigOptions.copyRowHeader"
            inputId="copy-row-header"
            name="copy-row-header"
            :binary="true"/>
          <div class="group-header">Copy row header</div>
        </div>
      </div>
      <div class="footer">
        <div class="empty"></div>
        <div class="footer-buttons">
          <CustomButton text="Cancel"
            @onClick="closeDialog"
            :margin-left="16"
            :type="ButtonType.OUTLINED"/>
          <CustomButton text="Save"
            @onClick="saveConfig"
            :margin-left="16"
            :type="ButtonType.POSITIVE"/>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style scoped lang="scss">
@import "../../../../assets/dialog";
:deep(.p-dialog) {
  max-height: 80vh;
}
:deep(.p-dialog-header) {
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.dialog-content {
  width: 600px;
  padding: 8px 40px 40px 40px;
  margin-bottom: 40px;
}
.enable-dynamic {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}
.enable-dynamic-text {
  margin-right: 24px;
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 9999;

  .footer-buttons {
    display: flex;
  }
}
.dynamic-options {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .group-header {
    margin-left: 8px;
    font-size: 14px;
  }
}
</style>
